import Alpine from 'alpinejs';

(window as typeof window & { Alpine: typeof Alpine }).Alpine = Alpine;

document.addEventListener('alpine:init', () => {
  Alpine.data('sulfadimethoxineCalculator', () => ({
    weight: 0,
    weightUnit: 'kg',
    species: 'dog',
    formulation: 'tablet',
    result: null,
    dosageDetails: '',
    warnings: [],

    init() {
      this.$watch('weight', () => this.calculate());
      this.$watch('weightUnit', () => this.calculate());
      this.$watch('species', () => this.calculate());
      this.$watch('formulation', () => this.calculate());
    },

    validate() {
      this.warnings = [];

      if (!this.weight || this.weight <= 0) {
        this.warnings.push('Please enter a valid weight');
        return false;
      }

      if (this.weightUnit === 'kg' && this.weight > 100) {
        this.warnings.push('Please verify weight - seems unusually high');
      }

      if (this.weightUnit === 'lb' && this.weight > 220) {
        this.warnings.push('Please verify weight - seems unusually high');
      }

      return true;
    },

    calculate() {
      if (!this.validate()) {
        this.result = null;
        this.dosageDetails = '';
        return;
      }

      // Convert weight to kg if needed
      let weightInKg = this.weightUnit === 'lb' ? this.weight * 0.453592 : this.weight;

      // Initial dosing rate is 55 mg/kg on day 1, then 27.5 mg/kg for subsequent days
      const initialDose = 55; // mg/kg
      const maintenanceDose = 27.5; // mg/kg

      // Calculate total doses needed
      const initialDoseMg = weightInKg * initialDose;
      const maintenanceDoseMg = weightInKg * maintenanceDose;

      if (this.formulation === 'tablet') {
        // Common tablet sizes: 125mg, 250mg, 500mg
        const tabletSizes = [125, 250, 500];
        const initialDoseResult = this.calculateTabletDose(initialDoseMg, tabletSizes);
        const maintenanceDoseResult = this.calculateTabletDose(maintenanceDoseMg, tabletSizes);

        this.result = initialDoseResult.text;
        this.dosageDetails = `Day 1: ${initialDoseResult.text} once daily\nDays 2-5: ${maintenanceDoseResult.text} once daily`;
      } else {
        // Liquid formulation (oral suspension typically 50mg/ml)
        const concentrationMgPerMl = 50;
        const initialMl = this.roundToSignificant(initialDoseMg / concentrationMgPerMl, 2);
        const maintenanceMl = this.roundToSignificant(maintenanceDoseMg / concentrationMgPerMl, 2);

        this.result = `${initialMl} mL`;
        this.dosageDetails = `Day 1: ${initialMl} mL once daily\nDays 2-5: ${maintenanceMl} mL once daily`;
      }
    },

    calculateTabletDose(doseMg: number, tabletSizes: number[]) {
      let result = {
        text: '',
        remainder: doseMg
      };

      // Sort tablets largest to smallest
      tabletSizes.sort((a, b) => b - a);

      for (const tabletSize of tabletSizes) {
        const count = Math.floor(result.remainder / tabletSize);
        if (count > 0) {
          if (result.text) result.text += ' + ';
          result.text += `${count} × ${tabletSize}mg`;
          result.remainder -= count * tabletSize;
        }
      }

      // Handle remaining amount as fraction if significant
      if (result.remainder > 5) {
        const closestTablet = this.findClosestTabletSize(result.remainder, tabletSizes);
        const fraction = this.roundToNearestFraction(result.remainder / closestTablet);
        if (fraction > 0) {
          if (result.text) result.text += ' + ';
          result.text += `${fraction} × ${closestTablet}mg`;
        }
      }

      return result;
    },

    findClosestTabletSize(target: number, tabletSizes: number[]) {
      return tabletSizes.reduce((prev, curr) =>
        Math.abs(curr - target) < Math.abs(prev - target) ? curr : prev
      );
    },

    roundToNearestFraction(num: number): number {
      const fractions = [0.25, 0.5, 0.75, 1];
      return fractions.reduce((prev, curr) =>
        Math.abs(curr - num) < Math.abs(prev - num) ? curr : prev
      );
    },

    roundToSignificant(num: number, decimals: number): number {
      return Number(Math.round(Number(num + 'e' + decimals)) + 'e-' + decimals);
    }
  }));
});

Alpine.start();
